import { useEffect, useRef, useState } from 'react';
import { cn } from '../lib/utils';

/**
 * The toolbar row under a page's header: tabs and filters on the left;
 * search, secondary actions and the primary action on the right
 * (docs/DESIGN.md).
 *
 * From the desktop breakpoint it sticks to the top of the window, 80px tall,
 * so its controls sit on the same line as the sidebar's logo (the sidebar's
 * logo row is also 80px). Once it is stuck, a hairline separates it from the
 * content scrolling underneath. On phones it stays in the page, because the
 * stacked controls would cover too much of the screen.
 *
 * `secondary` is a second row - a list's search and filters - that sticks
 * directly under the page's toolbar instead of at the top of the window. It is
 * SECONDARY_TOOLBAR_HEIGHT tall (a 44px control plus 18px below it), so the gap
 * above its controls (the toolbar's own 18px) matches the gap below. Give the
 * toolbar above it `hairline={false}`: the hairline belongs under the last row.
 */
export const TOOLBAR_HEIGHT = 80;
export const SECONDARY_TOOLBAR_HEIGHT = 62;

export function PageToolbar({ className, children, secondary = false, hairline = true, ...props }) {
  const sentinel = useRef(null);
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const el = sentinel.current;
    if (!el || typeof window.IntersectionObserver === 'undefined') return undefined;
    // A secondary row is stuck once it reaches the bottom of the 80px toolbar
    const observer = new window.IntersectionObserver(
      ([entry]) => setStuck(!entry.isIntersecting),
      { threshold: 0, rootMargin: secondary ? '-80px 0px 0px 0px' : '0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [secondary]);

  return (
    <>
      <div ref={sentinel} aria-hidden="true" className="h-0" />
      <div
        className={cn(
          // Bleed to the main column's edges so content can't show beside it
          // Wraps rather than running off the edge at tablet widths
          'flex flex-col flex-wrap lg:flex-row lg:items-center lg:justify-between gap-4',
          'lg:sticky lg:-mx-8 lg:px-8 lg:bg-background lg:box-border',
          secondary
            ? 'lg:top-20 lg:z-20 lg:pt-0 lg:pb-[18px]'
            : 'lg:top-0 lg:z-30 lg:min-h-20 lg:py-[18px]',
          // The hairline is an inset shadow so it adds no height and the centre line stays at 40px
          'lg:transition-shadow',
          stuck && hairline && 'lg:shadow-[inset_0_-1px_0_hsl(var(--line))]',
          className
        )}
        {...props}
      >
        {children}
      </div>
    </>
  );
}

/**
 * A list table whose header row sticks under the page's toolbar on desktop,
 * like the toolbar itself. Spread the three onto the table's Card, the Table
 * and the TableHeader:
 *
 *   <Card className={STICKY_TABLE.card}>
 *     <Table containerClassName={STICKY_TABLE.container}>
 *       <TableHeader {...stickyTableHeader()}>
 *
 * Nothing above the header clips on desktop (overflow would stop it
 * sticking), so its corners are drawn by .table-head-rounded: square, solid
 * cells with a page-coloured mask, so rows scrolling under it never show
 * through the corners. `below` is how far down it sticks: the toolbar, plus a
 * secondary search row if the page has one.
 */
export const STICKY_TABLE = {
  // !: Card's own rounded-tile would otherwise win, leaving a 16px card around a 12px header
  card: '!rounded-cards overflow-hidden lg:overflow-visible',
  container: 'lg:overflow-visible',
};

export function stickyTableHeader(below = TOOLBAR_HEIGHT) {
  return { className: 'lg:sticky lg:z-10 table-head-rounded', style: { top: below } };
}

